import { OptionsApi } from '@core/swagger-client';
import { TEST_WEBINAR_MAX_DURATION_MINUTES, testWebinarEndDeadline } from '@core/types';
import { Button } from '@core/ui';
import { extendWebinarDuration, setWebinarExtendedResult, useAppDispatch, useAppSelector, WebinarExtendedResult } from '@online-webinar/store';
import { useQuery } from '@tanstack/react-query';
import { DateTime, Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const RemainingTimeToEndPanel = () => {
    const dispatch = useAppDispatch();
    const remainingTimeToEnd = useAppSelector((state) => state.liveWebinar.remainingTimeToEnd);
    const showEndWebinarWarning = useAppSelector((state) => state.liveWebinar.showEndWebinarWarning);
    const webinarExtendedResult = useAppSelector((state) => state.liveWebinar.webinarExtendedResult);
    const tierId = useAppSelector((state) => state.liveWebinar.tierId);
    const startOn = useAppSelector((state) => state.liveWebinar.startOn);
    const warningOn = useAppSelector((state) => state.liveWebinar.warningOn);
    const isTest = useAppSelector((state) => state.liveWebinar.webinarData.isTest);
    const startedOn = useAppSelector((state) => state.liveWebinar.webinarData.startedOn);
    const endOn = useAppSelector((state) => state.liveWebinar.endOn);
    const serverTime = useAppSelector((state) => state.liveWebinar.serverTime);
    const [isExtendingWebinar, setIsExtendingWebinar] = useState(false);
    // Default to 30 minutes
    const [duration, setDuration] = useState<Duration>(Duration.fromMillis(30 * 60 * 1000));
    const { t } = useTranslation();

    const { data: tierData, error: tierError } = useQuery(OptionsApi.tier.get(tierId!, { enabled: !!tierId }));

    useEffect(() => {
        if (tierError || !tierData || !warningOn || !startOn) {
            // Unable to fetch tier data, default to 30 minutes
            setDuration(Duration.fromMillis(30 * 60 * 1000));
            return;
        }

        // A test webinar is capped at 15 minutes instead of the tier's maximum (spec §5.2); the server
        // applies the same rule, so the offer here can never exceed what it accepts.
        const maximumDuration = isTest ? Duration.fromObject({ minutes: TEST_WEBINAR_MAX_DURATION_MINUTES }) : Duration.fromISOTime(tierData.duration);
        const start = DateTime.fromISO(startOn);
        const warning = DateTime.fromISO(warningOn);
        const duration = warning.diff(start);
        const timeLeftForExtension = Duration.fromDurationLike({ minutes: Math.floor(maximumDuration.minus(duration).as('minutes')) - 1 }); // Subtract 1 minute buffer
        if (timeLeftForExtension.as('minutes') < 1) {
            // Less than 1 minute left for extension, disable extension
            setDuration(Duration.fromMillis(0));
            dispatch(setWebinarExtendedResult(WebinarExtendedResult.NotAvailable));
            return;
        }

        if (timeLeftForExtension.as('minutes') < 30) {
            setDuration(timeLeftForExtension);
        } else {
            setDuration(Duration.fromMillis(30 * 60 * 1000));
        }
    }, [tierData, tierError, warningOn, startOn, isTest]);

    // A started test webinar is force-ended by the server at min(StartedOn + 15 min, booking end)
    // (spec §5.2), earlier than the booking's EndOn, which includes the tier's Q&A/overrun run-out.
    // Count down to that same deadline so this matches the TestWebinarCountdown at the top of the page.
    const testDeadline = isTest ? testWebinarEndDeadline(startedOn, endOn) : null;
    const remainingTime =
        testDeadline !== null
            ? Duration.fromMillis(Math.max(0, testDeadline - (serverTime ? new Date(serverTime).getTime() : Date.now())))
            : Duration.fromMillis(remainingTimeToEnd || 0);
    const extendDuration = () => {
        setIsExtendingWebinar(true);
        dispatch(extendWebinarDuration(duration));
    };

    useEffect(() => {
        setIsExtendingWebinar(false);
        if (webinarExtendedResult === WebinarExtendedResult.Success) {
            const timeout = setTimeout(() => {
                dispatch(setWebinarExtendedResult(null));
            }, 5000);
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [webinarExtendedResult]);

    if (webinarExtendedResult === WebinarExtendedResult.Success) {
        // The timer will remove this message after 5 seconds
        return (
            <div className="absolute top-0 left-1/2 max-w-full bg-white rounded-b-lg shadow-4xl mx-2 -translate-x-1/2 z-1 motion-safe:animate-[appear_.4s_ease-out]">
                <p className="text-success-700 p-4">{t('Broadcaster.RemainingTimePanel.ExtensionSuccess')}</p>
            </div>
        );
    }

    if (!showEndWebinarWarning) {
        return <></>;
    }

    return (
        <div className="flex max-md:flex-col items-center bg-warning pb-1">
            <div className="flex-1 text-white px-4 py-1 text-center">
                {t('Broadcaster.RemainingTimePanel.Ending')} <span className="text-xl md:text-2xl">{remainingTime.toFormat('hh:mm:ss')}</span>
            </div>
            <div className="flex-1 max-md:w-full bg-white rounded-b-lg rounded-tl-lg px-4 py-1 max-md:ms-1 text-center">
                {(webinarExtendedResult === undefined || webinarExtendedResult === null) && (
                    <Button variant="primary-outline" onClick={extendDuration} isLoading={isExtendingWebinar}>
                        {t('Broadcaster.Button.ExtendDuration', { minutes: duration.as('minutes') })}
                    </Button>
                )}
                {webinarExtendedResult === WebinarExtendedResult.Failed && (
                    <span className="inline-block pt-2 2xl:pt-1">{t('Broadcaster.RemainingTimePanel.ExtensionFailed')}</span>
                )}
                {webinarExtendedResult === WebinarExtendedResult.NotAvailable && (
                    <span className="inline-block pt-2 2xl:pt-1">{t('Broadcaster.RemainingTimePanel.ExtensionNotAvailable')}</span>
                )}
            </div>
        </div>
    );
};
